import * as d3 from 'd3'

import { margin } from '@aytm/app/stats/page/question-views/vk/components/chart/lib/constants'
import { countLabelSize, initScaleProjection } from '@aytm/app/stats/page/question-views/vk/components/chart/lib/utils'
import {
  axisGridFormater,
  getVWArea,
  getVWExtrem,
  getVWGraphs,
  getVWPoints,
  getXExtrem
} from '@aytm/app/stats/page/question-views/vk/components/chart/lib/vankonan-utils'

const drawAxis = (root, opts) => {
  const { model, width, height, infographic } = opts
  const background = model?.backgroundColor

  root.append('rect').attr('width', '100%').attr('height', '100%').attr('fill', '#4c5a65')

  const panel = root.append('g').classed('panel', true)

  panel.append('rect').attr('x', 0).attr('y', 0).attr('width', margin.x).attr('height', height).attr('fill', background)

  panel
    .append('rect')
    .attr('x', 0)
    .attr('y', height - 20)
    .attr('width', width)
    .attr('height', height)
    .attr('fill', background)

  panel.append('rect').attr('x', 0).attr('y', 0).attr('width', width).attr('height', margin.y).attr('fill', background)

  const xScale = d3
    .scaleLinear()
    .domain(getXExtrem(model))
    .range([0, infographic ? 645 : 635])
  const yScale = d3.scaleLinear().domain(getVWExtrem(model)).range([0, 376])

  const xAxis = d3
    .axisBottom(xScale)
    .tickFormat(d => `$${d}`)
    .tickSize(0)

  const yAxis = d3
    .axisLeft(yScale)
    .tickFormat(d => {
      const v = parseFloat(String(d)) * 100
      return v % 20 === 0 ? `${v}%` : ''
    })
    .tickSize(0)

  const xa = root
    .append('g')
    .classed('axis-lines', true)
    .attr('class', 'x-axis')
    .attr('transform', `translate(${margin.x}, ${height - 20})`)
    .call(xAxis)

  const ya = root
    .append('g')
    .classed('axis-lines', true)
    .attr('class', 'y-axis')
    .attr('transform', `translate(${margin.x}, ${margin.y})`)
    .call(yAxis)

  xa.selectAll('text')
    .attr('font-size', '10px')
    .attr('dy', '1em')
    .attr('fill', infographic ? '#ffffff' : '#000000')
    .style('line-height', '16px')
    .style('font-family', 'Ubuntu, Calibri, Segoe, Helvetica, Arial')
    .style('font-size', 10)
    .style('fill', infographic ? '#ffffff' : '#000000')

  ya.selectAll('text')
    .attr('font-size', '10px')
    .attr('dx', '-2em')
    .attr('fill', infographic ? '#ffffff' : '#000000')
    .style('line-height', '16px')
    .style('font-family', 'Ubuntu, Calibri, Segoe, Helvetica, Arial')
    .style('font-size', 10)
    .style('fill', infographic ? '#ffffff' : '#000000')

  // Horizontal grid lines along the Y axis
  ya.selectAll('g.tick')
    .append('line')
    .classed('grid-line', true)
    .attr('x1', 0)
    .attr('y1', 0)
    .attr('x2', width)
    .attr('y2', 0)
    .attr('stroke', '#d8d9d9')
    .attr('stroke-width', 1)
    .attr('opacity', d =>
      axisGridFormater(model, {
        axis: 'y',
        value: d
      })
    )

  // Axis X label
  xa.append('text')
    .attr('x', width - 130)
    .attr('y', -10)
    .style('font-family', 'Ubuntu, Calibri, Segoe, Helvetica, Arial')
    .style('font-size', '10px')
    .style('line-height', '16px')
    .style('fill', infographic ? '#ffffff' : '#000000')
    .text(model?.xText || 'UNIT PRICE')

  // Axis Y label
  ya.append('text')
    .attr('x', 0)
    .attr('y', 0)
    .style('font-family', 'Ubuntu, Calibri, Segoe, Helvetica, Arial')
    .style('font-size', '10px')
    .style('line-height', '16px')
    .style('fill', infographic ? '#ffffff' : '#000000')
    .attr('transform', `translate(-10, 0) rotate(270)`)
    .text(model?.yText || '')

  // Small rectangle ???
  root
    .append('rect')
    .attr('x', width - 5)
    .attr('y', height - 18)
    .attr('width', 20)
    .attr('height', 20)
    .attr('fill', background)

  root.selectAll('.x-axis .domain').style('display', 'none')
}

export const toggleLegend = ({ color, el, root, infographic }) => {
  const key = el.attr('data-graph')
  const graph = root.select(`.graph-lines path#${key}`)

  // current visibility
  const isHidden = graph.style('display') === 'none' || graph.attr('display') === 'none'

  graph.style('display', isHidden ? null : 'none')

  // legend text in the same <g> as the square
  const legendGroup = d3.select(el.node().parentNode)
  const textSel = legendGroup.select('text')

  const activeTextColor = infographic ? '#ffffff' : '#000000'
  const dimColor = '#9d9fa2'

  textSel.style('fill', isHidden ? activeTextColor : dimColor)
  el.style('fill', isHidden ? color : dimColor)
}

const drawArea = ({ root, opts, scaleProjection }) => {
  const { height, width, model, palette, infographic } = opts

  const line = d3
    .line()
    // @ts-ignore
    .x(d => scaleProjection(d.x).x)
    // @ts-ignore
    .y(d => scaleProjection(d.y).y)

  const area = d3
    .area()
    .x(d => scaleProjection(d).x)
    .y0(margin.y)
    .y1(height - 20)

  const areaY = 2 * margin.y
  const a = root.append('g').classed('vw-area', true)
  const vwArea = getVWArea(model)

  a.append('path')
    .datum(vwArea.data)
    .attr('d', area)
    .style('stroke-width', 0)
    .style('fill', '#6a8493')
    .style('opacity', 0.8)

  // Curves (TooCheap, Cheap, NotExpensive, etc)
  const graphs = getVWGraphs(model)

  Object.entries(graphs).forEach(([key, graph], index) => {
    root
      .append('g')
      .classed('graph-lines', true)
      .append('path')
      .datum(graph.data)
      .attr('d', line)
      .attr('id', key)
      .style('stroke', palette[index % palette.length])
      .style('stroke-width', graph.stroke.width)
      .style('stroke-dasharray', graph.stroke.dash)
      .style('fill', 'none')

    // Button row layout (6 slots), same math as original
    const shift = index * ((width - margin.x) / 6)
    const _shift = key === 'NotExpensive' ? 22 : 0

    const buttonGroup = root.append('g').classed('vw-buttons', true)

    // colored square (button)
    const x = shift + margin.x / 2
    const y = 0
    const color = palette[index % palette.length]

    buttonGroup
      .append('rect')
      .classed('vw-button', true)
      .attr('x', x - _shift)
      .attr('y', y)
      .attr('width', 20)
      .attr('height', 14)
      .attr('data-graph', key)
      .attr('data-index', index)
      .style('fill', color)
      .style('cursor', 'pointer')
      .on('mousedown', function () {
        toggleLegend({
          root,
          color,
          el: d3.select(this),
          infographic
        })
      })

    // text label
    buttonGroup
      .append('text')
      .classed('vw-button-text', true)
      .attr('x', x + 25 - _shift)
      .attr('y', y + 12)
      .style('font-family', 'Ubuntu, Calibri, Segoe, Helvetica, Arial')
      .style('font-size', '12px')
      .style('line-height', '18px')
      .style('fill', infographic ? '#ffffff' : '#000000')
      .text(graph.text)
  })

  const x0 = scaleProjection(vwArea.data[0]).x
  const x1 = scaleProjection(vwArea.data[1]).x

  a.append('path')
    .attr(
      'd',
      `M${x0} ${areaY}L${x0 + 10} ${areaY - 5}` +
        `M${x0} ${areaY}L${x0 + 10} ${areaY + 5}` +
        `M${x0} ${areaY}L${x1} ${areaY}` +
        `M${x1} ${areaY}L${x1 - 10} ${areaY - 5}` +
        `M${x1} ${areaY}L${x1 - 10} ${areaY + 5}`
    )
    .style('stroke', '#ffffff')
    .style('stroke-width', 2)

  const vwAreaText = countLabelSize(vwArea.text, 16)
  const lineW = x1 - x0

  a.append('text')
    .attr('x', x0 + (lineW / 2 - vwAreaText.width / 2 + 10))
    .attr('y', areaY - vwAreaText.height / 2)
    .attr('font-family', 'Ubuntu, Calibri, Segoe, Helvetica, Arial')
    .attr('font-size', 16)
    .attr('fill', '#FFFFFF')
    .style('font-family', 'Ubuntu, Calibri, Segoe, Helvetica, Arial')
    .style('font-size', 16)
    .style('font-weight', 'lighter')
    .style('line-height', '14px')
    .style('fill', '#ffffff')
    .text(vwArea.text)

  const textSecondary = countLabelSize('Acceptable Range', 12)
  a.append('text')
    .attr('x', x0)
    .attr('y', areaY)
    .attr('transform', `translate(${lineW / 2 - textSecondary.width / 2 + 5}, ${textSecondary.height})`)
    .attr('font-family', 'Ubuntu, Calibri, Segoe, Helvetica, Arial')
    .attr('font-size', 12)
    .attr('font-weight', 'lighter')
    .attr('fill', '#FFFFFF')
    .style('font-family', 'Ubuntu, Calibri, Segoe, Helvetica, Arial')
    .style('font-size', 12)
    .style('font-weight', 'lighter')
    .style('fill', '#ffffff')
    .style('line-height', '14px')
    .style('opacity', 0.8)
    .text('Acceptable Range')
}

const drawPoint = ({
  root,
  scaleProjection,
  point,
  r,
  color,
  sr = null,
  textSize = 16,
  textOpacity = 1,
  textPos = 'top',
  textColor = '#ffffff'
}) => {
  const { x: px, y: py } = {
    x: scaleProjection(point.x).x,
    y: scaleProjection(point.y).y
  }

  const group = root.append('g')

  // main filled circle
  group.append('circle').attr('cx', px).attr('cy', py).attr('r', r).style('fill', color)

  // optional stroked ring
  let textY = textPos === 'bottom' ? -(r + textSize) : r + textSize / 2
  if (sr != null) {
    group.append('circle').attr('cx', px).attr('cy', py).attr('r', sr).style('stroke', color).style('fill', 'none')

    textY = textPos === 'bottom' ? -(sr + textSize) : sr + textSize / 2
  }

  // measure label to center it horizontally (like original: - width/2 + 10)
  const textS = countLabelSize(point.text, textSize)

  group
    .append('text')
    .classed('vw-point-text', true)
    .attr('x', px - textS.width / 2 + 10)
    .attr('y', py - textY)
    .attr('font-family', 'Ubuntu, Calibri, Segoe, Helvetica, Arial')
    .attr('font-size', textSize)
    .attr('font-weight', 'lighter')
    .attr('fill', textColor)
    .attr('opacity', textOpacity)
    .style('font-family', 'Ubuntu, Calibri, Segoe, Helvetica, Arial')
    .style('font-size', textSize)
    .style('font-weight', 'lighter')
    .style('line-height', '14px')
    .style('fill', textColor)
    .style('opacity', textOpacity)
    .text(point.text)

  return group
}

const drawLabel = ({ root, model, scaleProjection, point, labels, reverse = false }) => {
  const oldVW = model?.oldVW || false
  if (oldVW) return null

  const labelSize = countLabelSize(`<span style='font-weight: 400'>${point?.label?.text}</span>`)
  const propSize = countLabelSize(`<span style='font-weight: 600'>${point?.label?.prop}</span>`)

  const px = scaleProjection(point.x).x
  const py = scaleProjection(point.y).y

  const g = root.append('g')

  const dMain = reverse
    ? [
        `M ${px} ${py - 3}`,
        `L ${px - 10} ${py - labelSize.height}`,
        `L ${px - labelSize.width} ${py - labelSize.height}`,
        `L ${px - labelSize.width} ${py - labelSize.height * 3}`,
        `L ${px} ${py - labelSize.height * 3}`,
        `L ${px} ${py}`
      ].join(' ')
    : [
        `M ${px} ${py - 3}`,
        `L ${px + 10} ${py - labelSize.height}`,
        `L ${px + labelSize.width} ${py - labelSize.height}`,
        `L ${px + labelSize.width} ${py - labelSize.height * 3}`,
        `L ${px} ${py - labelSize.height * 3}`,
        `L ${px} ${py}`
      ].join(' ')

  g.append('path')
    .attr('d', dMain)
    .attr('fill', model?.print ? '#333' : '#333')

  g.append('text')
    .attr('x', reverse ? px - labelSize.width + 10 : px + 10)
    .attr('y', py - labelSize.height * 2 + 5)
    .attr('font-family', 'Ubuntu, Calibri, Segoe, Helvetica, Arial')
    .attr('font-size', '17')
    .attr('fill', '#FFFFFF')
    .style('font-family', 'Ubuntu, Calibri, Segoe, Helvetica, Arial')
    .style('font-size', '17px')
    .style('line-height', '18px')
    .style('cursor', 'default')
    .style('fill', '#FFFFFF')
    .text(point?.label?.text)
    .html(`<tspan style='font-weight: 400'>${point?.label?.text}</tspan>`)

  // prop box (green)
  const dProp = reverse
    ? [
        `M ${px - labelSize.width} ${py - labelSize.height}`,
        `L ${px - labelSize.width} ${py - labelSize.height * 3}`,
        `L ${px - labelSize.width - propSize.width} ${py - labelSize.height * 3}`,
        `L ${px - labelSize.width - propSize.width} ${py - labelSize.height}`,
        `L ${px - labelSize.width} ${py - labelSize.height}`
      ].join(' ')
    : [
        `M ${px + labelSize.width} ${py - labelSize.height}`,
        `L ${px + labelSize.width} ${py - labelSize.height * 3}`,
        `L ${px + labelSize.width + propSize.width} ${py - labelSize.height * 3}`,
        `L ${px + labelSize.width + propSize.width} ${py - labelSize.height}`,
        `L ${px + labelSize.width} ${py - labelSize.height}`
      ].join(' ')

  g.append('path').attr('d', dProp).attr('fill', '#bad746')

  g.append('text')
    .attr('x', reverse ? px - labelSize.width - propSize.width + 10 : px + labelSize.width + 10)
    .attr('y', py - labelSize.height * 2 + 5)
    .attr('font-family', 'Ubuntu, Calibri, Segoe, Helvetica, Arial')
    .attr('font-size', '17px')
    .attr('fill', '#000000')
    .style('font-family', 'Ubuntu, Calibri, Segoe, Helvetica, Arial')
    .style('font-size', '17px')
    .style('line-height', '18px')
    .style('cursor', 'default')
    .style('fill', '#000000')
    .text(point?.label?.prop)
    .html(`<tspan style='font-weight: 600'>${point?.label?.prop}</tspan>`)

  g.on('mouseover', function () {
    // @ts-ignore
    d3.select(this).moveToFront()
  })
  g.on('mouseout', function () {
    if (labels?.frontlabel) {
      // @ts-ignore
      d3.select(labels.frontlabel).moveToFront()
    }
  })
  g.on('mousedown', function () {
    if (labels) labels.frontlabel = this
  })

  return g
}

const drawPoints = ({ root, opts, labels, scaleProjection }) => {
  const { model } = opts
  const points = getVWPoints(model) || {}
  const store = {}

  if (!points || !Object.keys(points).length) return

  const toggleOrCreate = (propName, create) => {
    const el = store[propName]
    if (el) {
      const curr = el.style('display')
      el.style('display', curr === 'none' ? 'inline' : 'none')
    } else {
      store[propName] = create()
    }
  }

  const labelProps = {
    root,
    model,
    scaleProjection,
    labels
  }

  drawPoint({
    root,
    scaleProjection,
    point: points.toocheap,
    r: 6,
    color: '#ffffff'
  }).on('mousedown', () => {
    toggleOrCreate('toocheapLabel', () => drawLabel({ ...labelProps, point: points.toocheap }))
  })

  drawPoint({
    root,
    scaleProjection,
    point: points.tooexp,
    r: 6,
    color: '#ffffff'
  }).on('mousedown', () => {
    toggleOrCreate('tooexpLabel', () => drawLabel({ ...labelProps, point: points.tooexp, reverse: true }))
  })

  drawPoint({
    root,
    scaleProjection,
    point: points.optimal,
    r: 8,
    color: '#ffffff',
    sr: 16
  }).on('mousedown', () => {
    toggleOrCreate('optimalLabel', () => drawLabel({ ...labelProps, point: points.optimal, reverse: true }))
  })

  drawPoint({
    root,
    scaleProjection,
    point: points.indiff,
    r: 4,
    color: '#000000',
    sr: null,
    textSize: 12,
    textOpacity: 0.8,
    textPos: 'top',
    textColor: '#ffffff'
  }).on('mousedown', () => {
    toggleOrCreate('indiffLabel', () => drawLabel({ ...labelProps, point: points.indiff, reverse: true }))
  })
}

export const drawWestenDorpChart = (svgEl, opts) => {
  const { model, height, infographic } = opts
  const width = opts?.width - (infographic ? 4 : 14)

  const root = d3.select(svgEl)

  const xScale = d3
    .scaleLinear()
    .domain(getXExtrem(model))
    .range([0, infographic ? 645 : 635])

  const yScale = d3.scaleLinear().domain(getVWExtrem(model)).range([0, 376])
  const scaleProjection = initScaleProjection({ xScale, yScale })

  d3.selection.prototype.moveToFront = function () {
    return this.each(function () {
      if (this.parentNode) {
        this.parentNode.appendChild(this)
      }
    })
  }

  d3.selection.prototype.moveToBack = function () {
    return this.each(function () {
      if (this.parentNode) {
        const firstChild = this.parentNode.firstChild
        if (firstChild) {
          this.parentNode.insertBefore(this, firstChild)
        }
      }
    })
  }

  root.selectAll('*').remove()

  root
    .attr('class', 'axis')
    .attr('version', '1.1')
    .attr('xmlns', 'http://www.w3.org/2000/svg')
    .attr('xmlns:xlink', 'http://www.w3.org/1999/xlink')
    .attr('width', width)
    .attr('height', height)
    .attr('viewBox', `0 0 ${width} ${height}`)

  const labels = {
    frontlabel: null
  }

  drawAxis(root, opts)
  drawArea({ root, opts, scaleProjection })
  drawPoints({ root, opts, scaleProjection, labels })
}
